import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../api'
import type { SupportChannelType } from '@/types/auth'

export interface Company {
  id: string
  name: string
  slug: string
  domains: { id: string; domain: string }[]
  logo: string | null
  active: boolean
  createdAt: string
  updatedAt: string
  // Certificate fields (opcionais — populados via tela do super_admin
  // ou via /admin/companies/me pelo admin da empresa).
  certificateBackgroundUrl?: string | null
  certificateLogoUrl?: string | null
  certificateSignatureUrl?: string | null
  directorName?: string | null
  directorRole?: string | null
  includeGlobalSignature?: boolean
  // Badges configuradas pra aparecer no certificado da empresa. Setadas
  // exclusivamente pelo super_admin (via /super-admin → Empresas). No
  // PDF, o backend filtra por `user_badges` — só as conquistadas pelo
  // próprio aluno aparecem.
  certificateBadges?: Array<{
    order: number
    badge: { id: string; name: string; iconUrl: string | null; tier: string }
  }>
  // Canal de atendimento — alimenta o botão flutuante de suporte.
  supportChannelType?: SupportChannelType | null
  supportChannelUrl?: string | null
  supportChannelLabel?: string | null
  _count?: {
    users: number
    courses: number
  }
}

export interface CreateCompanyDTO {
  name: string
  slug: string
  domains?: string[]
  logo?: string
  // Campos de certificado (opcionais no create — geralmente preenchidos
  // depois via tela específica). Super_admin pode setar de cara se quiser.
  certificateBackgroundUrl?: string | null
  certificateLogoUrl?: string | null
  certificateSignatureUrl?: string | null
  directorName?: string | null
  directorRole?: string | null
  includeGlobalSignature?: boolean
  // Lista ordenada de badgeIds. No create só badges globais são aceitas
  // (a empresa ainda não tem id pra ter badges próprias).
  certificateBadgeIds?: string[]
  supportChannelType?: SupportChannelType | null
  supportChannelUrl?: string | null
  supportChannelLabel?: string | null
}

export interface UpdateCompanyDTO {
  name?: string
  slug?: string
  domains?: string[]
  logo?: string
  active?: boolean
  certificateBackgroundUrl?: string | null
  certificateLogoUrl?: string | null
  certificateSignatureUrl?: string | null
  directorName?: string | null
  directorRole?: string | null
  includeGlobalSignature?: boolean
  // Lista ordenada de badgeIds. `[]` limpa; `undefined` é no-op.
  // Aceita globais (companyId IS NULL) + badges da própria empresa.
  certificateBadgeIds?: string[]
  supportChannelType?: SupportChannelType | null
  supportChannelUrl?: string | null
  supportChannelLabel?: string | null
}

const COMPANIES_KEY = ['super-admin', 'companies']

export function useCompanies(enabled = true) {
  return useQuery<Company[]>({
    queryKey: COMPANIES_KEY,
    queryFn: async () => {
      const { data } = await api.get('/admin/companies')
      return data
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}

export function useCreateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dto: CreateCompanyDTO) => {
      const { data } = await api.post('/admin/companies', dto)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY })
    },
  })
}

export function useUpdateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...dto }: UpdateCompanyDTO & { id: string }) => {
      const { data } = await api.put(`/admin/companies/${id}`, dto)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY })
    },
  })
}

export function useDeactivateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/admin/companies/${id}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY })
    },
  })
}

// --- Vincular usuários em massa ---

export interface UnlinkedUser {
  id: string
  name: string
  email: string
  avatar?: string
}

export function useUnlinkedUsers(companyId: string | undefined) {
  return useQuery<UnlinkedUser[]>({
    queryKey: ['super-admin', 'unlinked-users', companyId],
    queryFn: async () => {
      const { data } = await api.get(`/admin/companies/${companyId}/unlinked-users`)
      return data
    },
    enabled: !!companyId,
  })
}

export function useLinkUsers() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, userIds }: { companyId: string; userIds: string[] }) => {
      const { data } = await api.post(`/admin/companies/${companyId}/link-users`, { userIds })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANIES_KEY })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'unlinked-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}
